import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const InfiniteJobDropdown = ({ 
  value, 
  onChange, 
  error, 
  helperText,
  disabled 
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchJobs = useCallback(async (pageNum = 0, search = '', append = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/jobpost/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pageNum, limit: 20, search }
      });

      const jobs = response.data.jobs || [];
      const pagination = response.data.pagination || {};
      
      // Filter open jobs
      const openJobs = jobs.filter(job => {
        const closingDate = new Date(job.jobClosedDate);
        const today = new Date();
        return closingDate > today;
      });

      setTotalCount(pagination.total || 0);
      
      if (append) {
        setOptions(prev => [...prev, ...openJobs]);
      } else {
        setOptions(openJobs);
      }

      const totalPages = Math.ceil((pagination.total || 0) / 20);
      setHasMore(pageNum < totalPages - 1);
      
    } catch (error) {
      //console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs(0, '', false);
  }, []);

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
      if (!loading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchJobs(nextPage, searchTerm, true);
      }
    }
  };

  const handleSearch = (event, value) => {
    setSearchTerm(value);
    setPage(0);
    setHasMore(true);
    fetchJobs(0, value, false);
  };

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      onInputChange={handleSearch}
      getOptionLabel={(option) => 
        typeof option === 'string' ? option : `${option.jobTitle} - ${option.jobId}`
      }
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Position Applied For"
          required
          error={!!error}
          helperText={helperText || (totalCount > 0 ? `Found ${totalCount} positions` : '')}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: 300 }
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body1">{option.jobTitle}</Typography>
            <Typography variant="caption" color="textSecondary">
              {option.department} • {option.location}
            </Typography>
          </Box>
        </Box>
      )}
      noOptionsText={searchTerm ? "No matching positions" : "No positions available"}
      fullWidth
    />
  );
};

export default InfiniteJobDropdown;